import { useState } from "react";
import { ThumbsDownIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useSendPortfolioHealthFeedback } from "@/hooks/mutations/useSendPortfolioHealthFeedback";
import {
  HEALTH_CATEGORIES,
  type HealthCategory,
  type PortfolioHealthReasonings,
  type PortfolioHealthScores,
} from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import {
  getHealthTier,
  healthTierToBadgeClasses,
  healthTierToLabel,
} from "@/lib/constants";
import { cn } from "@/lib/utils";

const CATEGORY_LABELS: Record<HealthCategory, string> = {
  volume: "Volume",
  velocity: "Velocity",
  quality: "Quality",
  engagement: "Engagement",
};

interface HealthCellProps {
  companyId: string;
  companyName: string;
  health: PortfolioHealthScores | null;
  jobId: string;
  jobTitle: string;
  reasons: PortfolioHealthReasonings | null;
  score: number;
}

export const HealthCell = ({
  companyId,
  companyName,
  health,
  jobId,
  jobTitle,
  reasons,
  score,
}: HealthCellProps) => {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const { mutate: sendFeedback, isPending: isSendingFeedback } =
    useSendPortfolioHealthFeedback();
  const successToast = useSuccessToast();
  const errorToast = useErrorToast();
  const tier = getHealthTier(score);
  const trimmedFeedback = feedback.trim();
  const scoreBreakdown = Object.fromEntries(
    HEALTH_CATEGORIES.map((category) => [category, health?.[category] ?? 0]),
  );

  // Categories with a real score, paired with their reasoning (if any).
  const breakdown = HEALTH_CATEGORIES.map((category) => ({
    category,
    score: health?.[category] ?? 0,
    reason: reasons?.[category]?.trim() ?? "",
  })).filter((entry) => entry.score > 0 || entry.reason !== "");

  const handleSubmitFeedback = () => {
    if (trimmedFeedback.length === 0) return;

    sendFeedback(
      {
        companyId,
        companyName,
        feedback: trimmedFeedback,
        health,
        jobId,
        jobTitle,
        reasons,
        score,
        scoreBreakdown,
      },
      {
        onSuccess: () => {
          successToast({ description: "Health score feedback sent." });
          setFeedback("");
          setIsFeedbackOpen(false);
        },
        onError: () => {
          errorToast({ description: "Failed to send health score feedback." });
        },
      },
    );
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex h-7 cursor-default items-center justify-center whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold",
              healthTierToBadgeClasses[tier],
            )}
          >
            {healthTierToLabel[tier]}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          className="max-w-[300px] border border-slate-200 bg-white p-3 text-slate-900 shadow-md"
        >
          <Column className="gap-2">
            <Row className="items-center justify-between gap-3">
              <Text className="text-xs font-semibold text-slate-500">
                {healthTierToLabel[tier]} · {score}/100
              </Text>
              <Button
                aria-label="Report health score issue"
                className="h-6 w-6 shrink-0 rounded-full p-0 text-slate-400 hover:bg-red-50 hover:text-red-600"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsFeedbackOpen(true);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <ThumbsDownIcon className="h-3.5 w-3.5" />
              </Button>
            </Row>
            {breakdown.length === 0 ? (
              <Text className="text-xs text-slate-400">
                No reasoning available
              </Text>
            ) : (
              breakdown.map(({ category, score, reason }) => {
                const categoryTier = getHealthTier(score);
                return (
                  <Column key={category} className="gap-0.5">
                    <Row className="items-center gap-1.5">
                      <span
                        className={cn(
                          "inline-flex h-5 min-w-[1.75rem] items-center justify-center rounded-full border px-1.5 text-xs font-semibold tabular-nums",
                          healthTierToBadgeClasses[categoryTier],
                        )}
                      >
                        {score}
                      </span>
                      <Text className="text-xs font-medium text-slate-700">
                        {CATEGORY_LABELS[category]}
                      </Text>
                    </Row>
                    {reason !== "" && (
                      <Text className="text-xs leading-snug text-slate-500">
                        {reason}
                      </Text>
                    )}
                  </Column>
                );
              })
            )}
          </Column>
        </TooltipContent>
      </Tooltip>
      <Dialog
        open={isFeedbackOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setFeedback("");
          setIsFeedbackOpen(nextOpen);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-left text-lg font-semibold text-slate-900">
              Report health score issue
            </DialogTitle>
          </DialogHeader>
          <Column className="gap-3">
            <Column className="gap-1">
              <Text className="text-sm font-medium text-slate-700">
                {companyName} · {jobTitle}
              </Text>
              <Text className="text-xs text-slate-500">
                {healthTierToLabel[tier]} · {score}/100
              </Text>
            </Column>
            <Textarea
              className="min-h-[120px] resize-y text-sm"
              onChange={(event) => {
                setFeedback(event.target.value);
              }}
              placeholder="What looks wrong about this score?"
              value={feedback}
            />
          </Column>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsFeedbackOpen(false);
              }}
              type="button"
              variant="outlineSecondary"
            >
              Cancel
            </Button>
            <Button
              disabled={isSendingFeedback || trimmedFeedback.length === 0}
              onClick={handleSubmitFeedback}
              type="button"
            >
              {isSendingFeedback ? "Sending..." : "Send feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
